import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import NotificationRecipient from "notifications/recipients/notification-recipient";
import NotificationRecipientSchema from "notifications/recipients/notification-recipient-schema";
import { z } from "zod";

export type EntityNotificationSummaryJSON = z.infer<
  typeof EntityNotificationSummarySchema.EntityNotificationSummaryJSONZ
>;
export type EntityNotificationSummaryParams = z.infer<
  typeof EntityNotificationSummarySchema.EntityNotificationSummaryZ
>;

/**
 * The entity-wide notifications feed's contract (GitHub issue #4438).
 *
 * It mirrors the API's `EntityNotificationSummaryResponse`, which deliberately has no field able to
 * carry a notification's text — no message, body, subheader, fine print, or message info. Keeping
 * the client schema exactly as narrow means a server that started sending content would fail this
 * schema rather than quietly render it.
 */
export default class EntityNotificationSummarySchema {
  public static readonly EntityNotificationSummaryJSONZ = z.object({
    id: z.uuid(),
    recipients: z.array(NotificationRecipientSchema.NotificationRecipientJSONZ),
    topicCategory: z.string().nullish(),
    topicId: z.uuid().nullish(),
    actionRequired: z.string().nullish(),
    createdDate: z.iso.datetime({ offset: true }).nullish(),
    senderId: z.uuid().nullish(),
    senderName: z.string().nullish(),
    isMessageNotification: z.boolean(),
  });

  public static readonly EntityNotificationSummaryZ = z.object({
    id: z.instanceof(Guid),
    recipients: z.array(z.instanceof(NotificationRecipient)),
    topicCategory: z.string().nullable(),
    topicId: z.instanceof(Guid).nullable(),
    actionRequired: z.string().nullable(),
    createdDate: z.instanceof(AHDate).nullable(),
    senderId: z.instanceof(Guid).nullable(),
    senderName: z.string().nullable(),
    isMessageNotification: z.boolean(),
  });

  public static validate(values: unknown): EntityNotificationSummaryParams {
    return parseOrThrow(EntityNotificationSummarySchema.EntityNotificationSummaryZ, values, {
      logError: true,
      schemaName: "EntityNotificationSummarySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): EntityNotificationSummaryJSON {
    return parseOrThrow(EntityNotificationSummarySchema.EntityNotificationSummaryJSONZ, values, {
      logError: true,
      schemaName: "EntityNotificationSummarySchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is EntityNotificationSummaryJSON {
    try {
      EntityNotificationSummarySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: EntityNotificationSummaryJSON): EntityNotificationSummaryParams {
    const parsed = parseOrThrow(EntityNotificationSummarySchema.EntityNotificationSummaryJSONZ, data, {
      logError: true,
      schemaName: "EntityNotificationSummarySchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return EntityNotificationSummarySchema.codec.decode(parsed);
  }

  public static serialize(summary: EntityNotificationSummaryParams): EntityNotificationSummaryJSON {
    const parsed = EntityNotificationSummarySchema.validate(summary);
    return EntityNotificationSummarySchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    EntityNotificationSummarySchema.EntityNotificationSummaryJSONZ,
    EntityNotificationSummarySchema.EntityNotificationSummaryZ,
    {
      decode: (data) => ({
        id: new Guid(GuidSchema.deserialize(data.id)),
        recipients: data.recipients.map((recipient) => NotificationRecipient.fromJSON(recipient)),
        topicCategory: data.topicCategory ?? null,
        topicId: data.topicId ? new Guid(GuidSchema.deserialize(data.topicId)) : null,
        actionRequired: data.actionRequired ?? null,
        createdDate: AHDate.fromJSON(data.createdDate) ?? null,
        senderId: data.senderId ? new Guid(GuidSchema.deserialize(data.senderId)) : null,
        senderName: data.senderName ?? null,
        isMessageNotification: data.isMessageNotification,
      }),
      encode: (summary) => ({
        id: summary.id.toJSON(),
        recipients: summary.recipients.map((recipient) => recipient.toJSON()),
        topicCategory: summary.topicCategory,
        topicId: summary.topicId?.toJSON(),
        actionRequired: summary.actionRequired,
        createdDate: summary.createdDate?.toJSON(),
        senderId: summary.senderId?.toJSON(),
        senderName: summary.senderName,
        isMessageNotification: summary.isMessageNotification,
      }),
    }
  );
}
